import React, { useState } from "react";
import { ReactComponent as GlobeClick } from "../../assets/icons/globe-06.svg";
import ModalBase from "./ModalBase";
import ChatMapDrawer from "./ChatMapDrawer";
import { geojsonToKML } from "../../utils/geojsonUtils";
import { usePostHogCapture } from "../../hooks/usePostHogCapture";
import { POSTHOG_EVENTS } from "../../utils/posthogEvents";

interface ChatMapDrawerModalProps {
  onClose?: (result?: any) => void;
}

/** Draw-on-map modal with Cancel/Confirm pinned in the ModalBase footer. Opened via ModalGlobal from the chat, and rendered inline (inside a local backdrop) by the schedule question editor, where opening a second global modal would unmount the schedule form. */
const ChatMapDrawerModal: React.FC<ChatMapDrawerModalProps> = ({ onClose }) => {
  const [drawnGeojson, setDrawnGeojson] = useState<any>(null);
  const { capture } = usePostHogCapture();

  const hasGeometry = Boolean(drawnGeojson?.features?.length);

  const handleConfirm = () => {
    if (!hasGeometry) return;

    try {
      let geojsonToConvert = drawnGeojson;

      // Auto-close an open polygon so the user doesn't need to click "Complete"
      const feature = drawnGeojson.features[0];
      if (feature?.geometry?.type === "Polygon") {
        const ring = feature.geometry.coordinates[0];
        if (ring && ring.length >= 3) {
          const first = ring[0];
          const last = ring[ring.length - 1];
          const isClosed = first[0] === last[0] && first[1] === last[1];
          if (!isClosed) {
            const closedFeature = {
              ...feature,
              geometry: { ...feature.geometry, coordinates: [[...ring, first]] },
            };
            geojsonToConvert = { ...drawnGeojson, features: [closedFeature] };
          }
        }
      }

      const kmlContent = geojsonToKML(geojsonToConvert);

      capture(POSTHOG_EVENTS.CHAT_MAP_GEOMETRY_ATTACHED, {
        geometry_type: drawnGeojson.features[0]?.geometry?.type,
        feature_count: drawnGeojson.features.length,
      });

      setDrawnGeojson(null);
      onClose?.({ type: "chatMapDraw", kmlContent, filename: "drawn-geometry.kml" });
    } catch (error) {
      console.error("Failed to convert geometry to KML:", error);
    }
  };

  return (
    <ModalBase
      title="Draw on Map"
      icon={<GlobeClick className="h-5 w-5" />}
      onClose={() => onClose?.()}
      size="2xl"
      fillHeight
      // Below 2xl's full-screen breakpoint (1200px + 32px margin), fill the visible viewport so the footer pins to the bottom of the screen instead of the bottom of the content.
      className="max-[1232px]:h-[100dvh] max-[1232px]:max-h-[100dvh]"
      footer={
        <>
          <button type="button" onClick={() => onClose?.()} className="eai-btn-outline">
            Cancel
          </button>
          <button type="button" onClick={handleConfirm} disabled={!hasGeometry} className="eai-btn-cta">
            Confirm
          </button>
        </>
      }
    >
      <ChatMapDrawer onGeojsonChange={setDrawnGeojson} />
    </ModalBase>
  );
};

export default ChatMapDrawerModal;
